import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType } from 'constants/enums'
import { isPlaylistJw } from 'utils/string'
import { ApiService } from 'services/api/api.service'
import { IVideoPlaylist } from './video-playlist'
import { PlaylistStore } from './store'
import { renderUIISODuration, renderUITimeAgo } from 'utils/time'
import { Nullable } from 'constants/types'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'
import { logger } from 'utils/system/logger'
import { getAlphaNumericRandomStr } from 'utils/string'

const { videoPlaylist: videoPlaylistClass } = CLASS_NAME
const PLAYLIST_NO_CONTENT_HEADER = 'No content available yet!'
const PLAYLIST_NO_CONTENT_MESSAGE =
    'Come back often to see if we have new content.'

export class Playlist extends AbstractObserver {
    public currentIndex = 0
    public _queueList: HTMLElement
    public _playlistId: string
    public _uniqueId: string
    public _propertyTag: string
    public _mainTag: string
    public _altTag: string
    public _showDesc: string
    private _playlistData: IVideoPlaylist
    private _mainTags: string[] = []

    /**
     * Get the playlistData
     */
    public get playlistData(): IVideoPlaylist {
        return this._playlistData
    }

    /**
     * Get the playlistId
     */
    public get playlistId(): string {
        return this._playlistId
    }

    /**
     * Get the uniqueId
     */
    public get uniqueId(): string {
        return this._uniqueId
    }

    /**
     * Get the propertyTag
     */
    public get propertyTag(): string {
        return this._propertyTag
    }

    /**
     * Get the mainTag
     */
    public get mainTag(): string {
        return this._mainTag
    }

    /**
     * Get the altTag
     */
    public get altTag(): string {
        return this._altTag
    }

    /**
     * Get the showDesc
     */
    public get showDesc(): string {
        return this._showDesc
    }

    /**
     * Get the playlist list section element
     */
    public get queueList(): HTMLElement {
        return this._queueList
    }

    /**
     * Get the playlist list button elements
     * get will only work after initial render
     */
    private get queueButtons(): HTMLElement[] {
        return this.getElements(`.${videoPlaylistClass.itemBtn}`, this.el)
    }

    /**
     * Get the playlist list button elements
     * get will only work after initial render
     */
    private get videoCount(): Nullable<HTMLElement> {
        return this.getElement(`.${videoPlaylistClass.numVideos}`, this.el)
    }

    /**
     * Initializes the video centre list.
     *
     * @param {HTMLElement} el
     * @param {HTMLElement} _queueList
     * @param {string} _playlistId
     * @param {string} _uniqueId
     */
    public constructor(protected el: HTMLElement) {
        super(el)
        let playlistId = el.dataset.playlistId
        if (!playlistId) {
            const newEl = this.getElement('[data-playlist-id]', el)
            if (newEl) {
                this.el = newEl
                playlistId = this.el.dataset.playlistId
            }
        }
        const uniqueId = this.el.dataset.uniqueId
        const propertyTag = this.el.dataset.propertyTag
        const mainTag = this.el.dataset.mainTag
        const altTag = this.el.dataset.altTag
        const showDesc = this.el.dataset.showDesc
        const queueList = this.getElement(
            `.${videoPlaylistClass.queue}`,
            this.el
        )
        const JW_ID_LENGTH = 8

        if (propertyTag && mainTag) {
            // fake id, it takes a unique id to keep the functionality of the playlist queue
            playlistId = getAlphaNumericRandomStr(JW_ID_LENGTH)
            this._propertyTag = propertyTag
            this._mainTag = mainTag
            // Parse comma-separated main tags
            this._mainTags = mainTag
                .split(',')
                .map((tag) => tag.trim())
                .filter((tag) => tag.length > 0)
            if (altTag) {
                this._altTag = altTag
            }
        }

        this._showDesc = 'False'
        if (showDesc) {
            this._showDesc = showDesc
        }

        if (!playlistId || !uniqueId || !queueList) {
            return
        }

        this._playlistId = playlistId
        this._uniqueId = uniqueId
        this._queueList = queueList

        PlaylistStore.getInstance().add(this)

        // Check if eager loading is enabled
        const isEagerLoad = this.el.hasAttribute('data-video-eager-load')
        if (isEagerLoad) {
            // Load immediately without waiting for IntersectionObserver
            this.render()
        } else {
            // Use lazy loading with IntersectionObserver
            this.activateObserver({ rootMargin: '200px' })
        }
    }

    /**
     * Load the list content, only once
     */
    public render(): void {
        // if the element has data-playlist-json, the server-side JSON data
        if (this.el.dataset.playlistJson) {
            this.el.classList.remove(CLASS_NAME.list.listLoading)
            this.activate(JSON.parse(this.el.dataset.playlistJson))
            return
        }

        if (this.propertyTag && this.mainTag) {
            this.fetchByTagsWithOr()
        } else {
            new ApiService()
                .fetchVideoPlaylist(this.playlistId)
                .then((data: IVideoPlaylist) => {
                    this.el.classList.remove(CLASS_NAME.list.listLoading)
                    this.activate(data)
                })
                .catch((err) => {
                    this.el.classList.remove(CLASS_NAME.list.listLoading)
                    this.deactivate()
                    logger('error', err)
                })
        }
    }

    /**
     * Fetch videos by combining all main tags with OR logic in a single API call
     * Falls back to altTag if main tags return no results
     */
    private fetchByTagsWithOr(): void {
        // Combine all main tags with comma separator for OR logic
        const combinedTags = this._mainTags.join(',')

        new ApiService()
            .fetchVideoPlaylistByTags(
                this.propertyTag,
                combinedTags,
                this.showDesc
            )
            .then((data: IVideoPlaylist) => {
                if (data.items.length > 0) {
                    this.el.classList.remove(CLASS_NAME.list.listLoading)
                    this.activate(data)
                } else if (this.altTag) {
                    // Fallback to altTag if no results from main tags
                    this.fetchByAltTag()
                } else {
                    this.el.classList.remove(CLASS_NAME.list.listLoading)
                    this.addNoContentMessage()
                }
            })
            .catch((err) => {
                this.el.classList.remove(CLASS_NAME.list.listLoading)
                this.deactivate()
                logger('error', err)
            })
    }

    /**
     * Fetch videos by altTag as fallback
     */
    private fetchByAltTag(): void {
        new ApiService()
            .fetchVideoPlaylistByTags(
                this.propertyTag,
                this.altTag,
                this.showDesc
            )
            .then((data: IVideoPlaylist) => {
                this.el.classList.remove(CLASS_NAME.list.listLoading)
                if (data.items.length > 0) {
                    this.activate(data)
                } else {
                    this.addNoContentMessage()
                }
            })
            .catch((err) => {
                this.el.classList.remove(CLASS_NAME.list.listLoading)
                this.deactivate()
                logger('error', err)
            })
    }

    /**
     * Initializer
     * queueList is already verified in constructor
     *
     * @param {IVideoPlaylist}
     */
    private activate(data: IVideoPlaylist): void {
        // Item is empty, deactivate
        if (!data || !data.items || !data.items.length) {
            this.deactivate()
            return
        }

        this._playlistData = data
        PlaylistStore.getInstance().initLeadVideo()

        // update the queue list with the remaining videos, up to limit
        this.queueList.innerHTML = this.renderPlaylistQueue()

        // attach events to newly created buttons
        if (this.queueButtons) {
            this.queueList.addEventListener(EventType.CLICK, (ev: MouseEvent) =>
                this.onClick(ev)
            )
        }

        // n Video(s)
        if (this.videoCount) {
            this.videoCount.innerHTML =
                1 === this._playlistData.pageInfo.totalResults
                    ? '1 Video'
                    : `${this._playlistData.pageInfo.totalResults} Videos`
        }

        // Show elements that should be visible when videos load successfully
        this.showElementsOnLoad()

        return
    }

    /**
     * Show elements with data-show-on-video-load attribute when videos load successfully
     */
    private showElementsOnLoad(): void {
        const elements = document.querySelectorAll<HTMLElement>(
            `[${ATTR_NAME.video.playlist.showOnVideoLoad}]`
        )
        elements.forEach((element) => {
            element.hidden = false
        })
    }

    /**
     * Loading failed
     */
    private deactivate(): void {
        this.el.remove()
        PlaylistStore.getInstance().remove(this.playlistId)
    }

    /***
     * Add no content section when no videos available
     */
    private addNoContentMessage(): void {
        // Use closest() to find parent section element with data-hide-selector
        const wrapper = this.el.closest(
            `.${CLASS_NAME.videoPlaylist._}`
        ) as HTMLElement | null

        const hideSelector = wrapper
            ? wrapper.getAttribute(ATTR_NAME.video.playlist.videoHideSelector)
            : undefined
        if (hideSelector) {
            // Support comma-separated selectors
            const selectors = hideSelector.split(',').map((s) => s.trim())
            selectors.forEach((selectorItem) => {
                // If selector doesn't start with # or ., assume it's an ID
                const selector =
                    selectorItem.startsWith('#') || selectorItem.startsWith('.')
                        ? selectorItem
                        : `#${selectorItem}`
                const hideElement =
                    document.querySelector<HTMLElement>(selector)
                if (hideElement) {
                    hideElement.style.display = 'none'
                }
            })
            // Also hide the playlist wrapper itself
            if (wrapper) {
                wrapper.style.display = 'none'
            }
        } else {
            const playlistWrapper = document.getElementById(
                ATTR_NAME.widget.videoPlaylist.id
            )
            playlistWrapper?.setAttribute(
                'class',
                'row video-playlist__wrapper'
            )
            const title = document.getElementsByClassName(
                CLASS_NAME.videoPlaylist.leadvideo.sectionTitle
            )[0]
            while (playlistWrapper?.firstChild) {
                playlistWrapper.removeChild(playlistWrapper.firstChild)
            }
            title.classList.add('col-xs-12', 'col-lg-8', 'offset-lg-2')
            playlistWrapper?.append(title)
            const messageContainer = document.createElement('div')
            messageContainer.className = `col-xs-12 col-lg-8 offset-lg-2 ${CLASS_NAME.list.listWidgetContentUnavailable}`
            const headerMessage = document.createElement('p')
            const headerMessageStrong = document.createElement('strong')
            headerMessageStrong.innerHTML = PLAYLIST_NO_CONTENT_HEADER
            headerMessage.appendChild(headerMessageStrong)
            messageContainer.appendChild(headerMessage)
            const message = document.createElement('p')
            message.innerHTML = PLAYLIST_NO_CONTENT_MESSAGE
            messageContainer.appendChild(message)
            playlistWrapper?.appendChild(messageContainer)
        }
    }

    /**
     * Build the list items for the playlist
     *
     * @return {string} HTML
     */
    private renderPlaylistQueue(): string {
        const items = this._playlistData.items
        const listItems: string[] = []

        // generate item list HMTL for remaining items
        items.forEach((_, index) => {
            // account for slicing in passed index
            const playlistItem = this.renderPlaylistItem(index)
            listItems.push(playlistItem)
        })

        if (PlaylistStore.getInstance().isCurrentList(this)) {
            // Move the first item to the end
            listItems.push(listItems.splice(0, 1)[0])
        }

        return listItems.join('')
    }

    /**
     * Build the list items for the playlist
     *
     * @param {number} index Item index
     * @return {string} HTML
     */
    private renderPlaylistItem(index: number): string {
        const item = this._playlistData.items[index]
        const defaultSrcset: string = item.thumbnails.default
            ? `${item.thumbnails.default.url} ${item.thumbnails.default.width}w,`
            : ''
        const mediumSrcset: string = item.thumbnails.medium
            ? `${item.thumbnails.medium.url} ${item.thumbnails.medium.width}w,`
            : ''
        const highSrcset: string = item.thumbnails.high
            ? `${item.thumbnails.high.url} ${item.thumbnails.high.width}w,`
            : ''
        const standardSrcset: string = item.thumbnails.standard
            ? `${item.thumbnails.standard.url} ${item.thumbnails.standard.width}w`
            : ''

        const defaultWebpSrcset: string =
            item.thumbnails.default && item.thumbnails.default.urlWebp
                ? `${item.thumbnails.default.urlWebp} ${item.thumbnails.default.width}w,`
                : ''
        const mediumWebpSrcset: string =
            item.thumbnails.medium && item.thumbnails.medium.urlWebp
                ? `${item.thumbnails.medium.urlWebp} ${item.thumbnails.medium.width}w,`
                : ''
        const highWebpSrcset: string =
            item.thumbnails.high && item.thumbnails.high.urlWebp
                ? `${item.thumbnails.high.urlWebp} ${item.thumbnails.high.width}w,`
                : ''
        const standardWebpSrcset: string =
            item.thumbnails.standard && item.thumbnails.standard.urlWebp
                ? `${item.thumbnails.standard.urlWebp} ${item.thumbnails.standard.width}w,`
                : ''
        const videoPlayerIconClass = isPlaylistJw(this.playlistId)
            ? ''
            : videoPlaylistClass.itemImgYT

        return `
            <li class="${videoPlaylistClass.item}"  ${
            ATTR_NAME.main.carousel.slideItem
        }>
                <button
                    type="button"
                    class="${videoPlaylistClass.itemBtn}"
                    ${ATTR_NAME.video.playlist.index}="${index}"
                    ${ATTR_NAME.video.playlist.videoPlaylistId}="${
            this.playlistId
        }"
                >
                    <div>
                        <picture class="${
                            videoPlaylistClass.itemImg
                        } ${videoPlayerIconClass}">
                            <source sizes="(min-width: 1200px) 10vw, (max-width: 480px) 50vw, 30vw"
                            srcset="${defaultWebpSrcset} ${mediumWebpSrcset} ${highWebpSrcset} ${standardWebpSrcset}" type="image/webp" />
                            <source sizes="(min-width: 1200px) 10vw, (max-width: 480px) 50vw, 30vw"
                            srcset="${defaultSrcset} ${mediumSrcset} ${highSrcset} ${standardSrcset}" type="image/jpg" />
                            <img src="${
                                item.thumbnails.default.url
                            }" height="60" width="74" alt="" />
                            <div class="${
                                videoPlaylistClass.itemImgOverlay
                            }"></div>
                        </picture>
                        <div class="${videoPlaylistClass.itemContent}">
                            <span class="${videoPlaylistClass.itemTitle}">
                                <span class="${
                                    CLASS_NAME.common.visuallyHidden
                                }">play video: </span>
                                <span class="title-clamp">${item.title}</span>
                            </span>

                            <div class="${videoPlaylistClass.meta}">
                                <p class="${videoPlaylistClass.metaPubtime}">
                                    ${renderUITimeAgo(item.publishedAt)}
                                </p>
                                <p class="video-playlist__queue-item-duration">
                                    ${renderUIISODuration(item.duration)}
                                </p>
                            </div>
                        </div>
                    </div>
                </button>
            </li>`
    }

    /**
     * Handle the Event for clicking a queued video button
     *
     * @param {any} event Event object
     */
    private onClick(event: MouseEvent): void {
        // user has interacted!
        const btn = (event.target as HTMLElement).closest(
            `.${videoPlaylistClass.itemBtn}`
        )

        // type as number
        const index = btn
            ? btn.getAttribute(ATTR_NAME.video.playlist.index)
            : false
        const currentIndex = index ? parseFloat(index) : -1

        if (currentIndex === -1) {
            return
        }

        this.currentIndex = currentIndex

        PlaylistStore.getInstance().onClick(this.playlistId, currentIndex)
    }

    public removeNowPlaying(): void {
        if (!this.queueButtons) {
            return
        }

        this.queueButtons.forEach((button: HTMLElement) => {
            button.classList.remove(videoPlaylistClass.nowPlaying)
        })
    }

    public addNowPlaying(index: number): void {
        const element = this.getElement(
            `.${videoPlaylistClass.itemBtn}[${ATTR_NAME.video.playlist.index}="${index}"]`,
            this.el
        )

        if (element) {
            element.classList.add(videoPlaylistClass.nowPlaying)
        }
    }
}
